import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";
import { useState } from "react";
import { Collapse, Icon } from "@mui/material";
import MDBox from "components/MDBox";
import UiButton from "components/base/UiButton";
import UiTypography from "components/base/UiTypography";
import { WorksheetCostCategory, WorksheetHeaderUnit } from "generated/graphql";
import { draftColors } from "modules/proposals/draft/draftDesignTokens";
import formatCentsToUSD from "utils/money/formatCentsToUSD";
import { WorksheetItemInput } from "DDD/action-objects/WorksheetItemUpdate";
import WorksheetCostRow from "./WorksheetCostRow";
import WorksheetLibraryPickerDialog from "./WorksheetLibraryPickerDialog";
import { HelperResolution } from "utils/worksheet/worksheetHelpers";
import { worksheetRowGridTemplate } from "./worksheetStyles";
import {
  WORKSHEET_CATEGORY_LABELS,
  WorksheetLibrarySelection,
  getRowCost,
} from "./worksheetFormValues";

export type WorksheetCostSectionRow = {
  key: string;
  index: number;
  row: WorksheetItemInput["costRows"][number];
};

type WorksheetCostSectionProps = {
  category: WorksheetCostCategory;
  rows: WorksheetCostSectionRow[];
  lineQuantity: string;
  lineUnit: WorksheetHeaderUnit;
  isReadOnly?: boolean;
  onAdd: () => void;
  onDuplicate: (index: number) => void;
  onDelete: (index: number) => void;
  onMove: (from: number, to: number) => void;
  resolution: HelperResolution;
  onAddSelections?: (selection: WorksheetLibrarySelection) => void;
};

export default function WorksheetCostSection({
  category,
  rows,
  lineQuantity,
  lineUnit,
  isReadOnly,
  onAdd,
  onDuplicate,
  onDelete,
  onMove,
  resolution,
  onAddSelections,
}: WorksheetCostSectionProps) {
  const [collapsed, setCollapsed] = useState(true);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const categoryTotal = rows.reduce(
    (sum, { row }) => sum + getRowCost(row, lineQuantity, lineUnit, resolution),
    0
  );

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || result.destination.index === result.source.index) return;

    const from = rows[result.source.index].index;
    const to = rows[result.destination.index].index;
    onMove(from, to);
  };

  const headers = [
    "",
    "Name",
    category === WorksheetCostCategory.TRUCKING ? "Trucks" : "Quantity",
    category === WorksheetCostCategory.TRUCKING ? "Hours / Qty" : "Unit",
    category === WorksheetCostCategory.TRUCKING ? "Unit" : "",
    "Rate",
    "Total",
  ];

  return (
    <MDBox
      sx={{
        border: `1px solid ${draftColors.border}`,
        borderRadius: "8px",
        overflow: "hidden",
      }}
    >
      <MDBox
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        px="8px"
        py="6px"
        sx={{ backgroundColor: draftColors.surfaceMuted }}
      >
        <MDBox
          display="flex"
          alignItems="center"
          gap="6px"
          sx={{ cursor: "pointer" }}
          onClick={() => setCollapsed((previous) => !previous)}
        >
          <Icon fontSize="small">{collapsed ? "chevron_right" : "expand_more"}</Icon>
          <UiTypography uiVariant="body-sm" sx={{ fontWeight: 600 }}>
            {WORKSHEET_CATEGORY_LABELS[category]}
          </UiTypography>
          {rows.length > 0 && (
            <UiTypography uiVariant="body-sm" sx={{ color: draftColors.textSecondary }}>
              {formatCentsToUSD(categoryTotal)}
            </UiTypography>
          )}
        </MDBox>
        {!isReadOnly && (
          <MDBox display="flex" alignItems="center" gap="4px">
            <UiButton
              uiVariant="outlined-small"
              onClick={(event) => {
                event.stopPropagation();
                setCollapsed(false);
                setLibraryOpen(true);
              }}
            >
              <Icon fontSize="small">library_add</Icon>
              Library
            </UiButton>
            <UiButton
              uiVariant="outlined-small"
              onClick={(event) => {
                event.stopPropagation();
                setCollapsed(false);
                onAdd();
              }}
            >
              <Icon fontSize="small">add</Icon>
              Add row
            </UiButton>
          </MDBox>
        )}
      </MDBox>

      <Collapse in={!collapsed}>
        <MDBox p="4px" display="flex" flexDirection="column" gap="4px">
          {rows.length > 0 && (
            <MDBox
              display="grid"
              gridTemplateColumns={worksheetRowGridTemplate}
              gap="4px"
              px="7px"
              pb="2px"
              sx={{ borderBottom: `1px solid ${draftColors.border}` }}
            >
              {headers.map((label, index) => (
                <UiTypography
                  key={`${label}-${index}`}
                  uiVariant="label-xs"
                  sx={{
                    color: draftColors.textSecondary,
                    textAlign: [2, 5, 6].includes(index) ? "right" : "left",
                    pr: index === 6 && !isReadOnly ? "30px" : 0,
                  }}
                >
                  {label}
                </UiTypography>
              ))}
            </MDBox>
          )}
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId={`worksheet-${category}`}>
              {(provided) => (
                <MDBox
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  display="flex"
                  flexDirection="column"
                  gap="4px"
                >
                  {rows.map(({ key, index, row }, sectionIndex) => (
                    <Draggable
                      key={key}
                      draggableId={key}
                      index={sectionIndex}
                      isDragDisabled={isReadOnly}
                    >
                      {(dragProvided) => (
                        <MDBox ref={dragProvided.innerRef} {...dragProvided.draggableProps}>
                          <WorksheetCostRow
                            index={index}
                            row={row}
                            lineQuantity={lineQuantity}
                            lineUnit={lineUnit}
                            isReadOnly={isReadOnly}
                            resolution={resolution}
                            onDuplicate={() => onDuplicate(index)}
                            onDelete={() => onDelete(index)}
                            onAddSelections={onAddSelections}
                            dragHandle={
                              <MDBox {...dragProvided.dragHandleProps} sx={{ display: "flex" }}>
                                <Icon fontSize="small">drag_indicator</Icon>
                              </MDBox>
                            }
                          />
                        </MDBox>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </MDBox>
              )}
            </Droppable>
          </DragDropContext>
        </MDBox>
      </Collapse>

      <WorksheetLibraryPickerDialog
        open={libraryOpen}
        category={category}
        onClose={() => setLibraryOpen(false)}
        onSelect={(selection) => {
          setCollapsed(false);
          onAddSelections?.(selection);
          setLibraryOpen(false);
        }}
        onSelectMany={(selections) => {
          setCollapsed(false);
          selections.forEach((selection) => onAddSelections?.(selection));
        }}
      />
    </MDBox>
  );
}
